import {Button} from "@radix-ui/themes";
import {Carousel, Divider, Modal, Typography} from "antd";
import React, {useEffect, useState} from "react";
import toast from "react-hot-toast";
import {useTranslation} from "react-i18next";
import {IconContext} from "react-icons";
import {FaArrowLeft} from "react-icons/fa";
import {FaXmark} from "react-icons/fa6";
import {useNavigate, useParams} from "react-router-dom";
import styled from "styled-components";
import FaceIdIcon from "../../../assets/wrinkle_analysis.png";
import EyeWrinkle from "../../../assets/wrinkles/eyewrinkle.png";
import LipWrinkle from "../../../assets/wrinkles/lipwrinkle.png";
import ForeheadWrinkle from "../../../assets/wrinkles/wrinkle.png";
import {AddButtonWithIcon} from "../../../components/AntCostomComponents/Buttons";
import {DashboardWrapper} from "../../../components/DashboardWrapper/DashboardWrapper.styles";
import {getAllAnalysis, getPatientFilesList, postWirnklesAnalysis,} from "../../../utils/api-lists";
import {filterFileName} from "../../../utils/general.util";
import {DivRow} from "../../../widgets/GeneralBody";
import PlaceholderView from "../components/PlaceholderView";
import {Header, ImageField, Images} from "../styles";
import AnalysisCard from "./components/analysis-card";
import WrinklesComponent from "./analysis/wrinkles.component";
import AcneDetailsComponent from "./analysis/acne-details.component";
import LoadingSpinner from "../../../components/AntCostomComponents/LoadingSpinner";

const {Title, Text} = Typography;

const Wrinkles = () => {
    const {patientId} = useParams();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [files, setFiles] = useState([]);
    const [analysisFiles, setAnalysisFiles] = useState([]);
    const [groupFiles, setGroupFiles] = useState({});
    const [selectedAnalysis, setSelectedAnalysis] = useState([]);
    const [selectedFile, setSelectedFile] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [isCompare, setIsCompare] = useState(false);
    const [filesToCompare, setFilesToCompare] = useState([]);
    const {t} = useTranslation("common");
    const slider = React.useRef(null);

    // New states for wrinkles analysis
    const [isWrinklesModalOpen, setIsWrinklesModalOpen] = useState(false);
    const [wrinklesAnalysisLoading, setWrinklesAnalysisLoading] = useState(false);
    const [wrinklesAnalysisResult, setWrinklesAnalysisResult] = useState(null);
    const [flipImage, setFlipImage] = useState(false);

    function toggleModal(key) {
        setSelectedAnalysis(groupFiles[key]);
        setIsOpen(!isOpen);
        console.log("selectedAnalysis", groupFiles[key]);
    }

    function toggleWrinklesModal() {
        setIsWrinklesModalOpen(!isWrinklesModalOpen);
    }

    const groupAnalysisByImageId = (analysis) => {
        const grouped = {};

        analysis.forEach((item) => {
            const {image_id, analysis_type, id} = item;

            if (image_id) {
                if (!grouped[image_id]) {
                    grouped[image_id] = [];
                }
                grouped[image_id].push(item);
            } else {
                const typeKey = id;
                if (!grouped[typeKey]) {
                    grouped[typeKey] = [];
                }
                grouped[typeKey].push(item);
            }
        });

        // sort by analysis type
        Object.keys(grouped).forEach((key) => {
            grouped[key].sort((a, b) => {
                return a.analysis_type - b.analysis_type;
            });
        });

        console.log("Grouped analysis:", grouped);
        return grouped;
    };

    async function getPatientFiles() {
        if (isLoading) <LoadingSpinner/>;
        setIsLoading(true);
        try {
            const patientFilesResponse = await getPatientFilesList(patientId);
            setFiles(patientFilesResponse.data);

            const wrinklesAnalysisResponse = await getAllAnalysis(patientId);
            console.log("data", wrinklesAnalysisResponse.data);
            if (wrinklesAnalysisResponse?.data) {
                setAnalysisFiles(wrinklesAnalysisResponse.data);
                const groupedFiles = groupAnalysisByImageId(wrinklesAnalysisResponse.data);
                setGroupFiles(groupedFiles);
            }
        } catch (e) {
            console.log(e);
        } finally {
            setIsLoading(false);
        }
    }

    const convertImageToBase64 = (imageFile) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(imageFile);
            reader.onloadend = () => {
                resolve(reader.result.split(",")[1]); // Get the Base64 string without the prefix
            };
            reader.onerror = (error) => {
                reject(error);
            };
        });
    };

    async function performWrinklesAnalysis() {
        if (wrinklesAnalysisLoading || !selectedFile) return;

        setWrinklesAnalysisLoading(true);
        setIsWrinklesModalOpen(true);

        try {
            const fetchImageResponse = await fetch(selectedFile["url"], {
                headers: {
                    "Access-Control-Allow-Origin": "*",
                },
            });
            const imageBlob = await fetchImageResponse.blob();

            const imageFile = new File([imageBlob], selectedFile["image_name"], {
                type: fetchImageResponse.headers.get("content-type") || "image/jpeg",
            });
            const base64Image = await convertImageToBase64(imageFile);

            const response = await toast.promise(postWirnklesAnalysis(base64Image), {
                loading: t("analyzing_wrinkles"),
                success: t("analysis_complete"),
                error: (e) =>
                    `${t("analysis_failed")}: ${
                        e.response?.data?.message || t("unknown_error")
                    }`,
            });

            setWrinklesAnalysisResult(response.data);
        } catch (e) {
            console.error("Error performing wrinkles analysis:", e);
        } finally {
            setWrinklesAnalysisLoading(false);
        }
    }

    function clearCompare() {
        setSelectedFile([]);
        setIsCompare(false);
    }

    useEffect(() => {
        document.title = "Visual analysis";
    }, []);

    useEffect(() => {
        getPatientFiles();
    }, [patientId]);

    const ImageWrapper = ({file}) => {
        return (
            <ImageField onClick={() => toggleModal(file)}>
                <img src={file["url"]} alt="Image"/>
                <p>{filterFileName(file["image_name"])}</p>
            </ImageField>
        );
    };

    const showWithFileType = () => {
        switch (selectedFile["type"]) {
            case "video":
                return (
                    <video controls>
                        <source src={selectedFile["url"]} type="video/mp4"/>
                        Your browser does not support the video tag.
                    </video>
                );
            default:
            case "image":
                return <img src={selectedFile["url"]} alt="Image"/>;
        }
    };

    const toggleFlip = () => {
        setFlipImage(!flipImage);
    };

    // Render wrinkles analysis result
    const renderWrinklesAnalysisResult = () => {
        if (!wrinklesAnalysisResult) return null;

        const results = wrinklesAnalysisResult?.wrinkle_analysis ?? {};
        const mask = wrinklesAnalysisResult?.wrinkle_mask;

        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {mask && (
                    <div
                        className="text-center flex justify-between gap-3 rounded-xl overflow-hidden relative"
                        style={{maxHeight: "25rem"}}
                    >
                        <Button
                            type="primary"
                            className="absolute left-3 top-3"
                            onClick={toggleFlip}
                        >
                            Flip
                        </Button>
                        {!flipImage ? (
                            <img
                                src={selectedFile["url"]}
                                alt="Image"
                                style={{objectFit: "cover", width: "100%", height: "100%"}}
                            />
                        ) : (
                            <img
                                src={mask}
                                alt="Image"
                                style={{objectFit: "cover", width: "100%", height: "100%"}}
                            />
                        )}
                        {/*<img src={mask} alt="Wrinkles Analysis Mask" className="max-w-full"*/}
                        {/*     style={{objectFit: 'cover', width: '100%', height: '100%'}}/>*/}
                    </div>
                )}

                <WrinkleWrapper className="flex flex-col gap-3 justify-between items-start bg-white p-3 rounded-xl">
                    <h3 className="font-bold text-center text-black">Wrinkles level</h3>
                    {WrinkleField({
                        icon: EyeWrinkle,
                        title: "Eye",
                        area: results["crows_feet"]["area"],
                        skeleton: results["crows_feet"]["skeleton_length"],
                        perimeter: results["crows_feet"]["perimeter"],
                        average: results["crows_feet"]["avg_width"],
                    })}
                    {WrinkleField({
                        icon: LipWrinkle,
                        title: "Lips",
                        area: results["lips"]["area"],
                        skeleton: results["lips"]["skeleton_length"],
                        perimeter: results["lips"]["perimeter"],
                        average: results["lips"]["avg_width"],
                    })}
                    {WrinkleField({
                        icon: ForeheadWrinkle,
                        title: "Forehead",
                        area: results["forehead"]["area"],
                        skeleton: results["forehead"]["skeleton_length"],
                        perimeter: results["forehead"]["perimeter"],
                        average: results["forehead"]["avg_width"],
                    })}
                </WrinkleWrapper>
            </div>
        );
    };

    const WrinkleField = ({
                              icon,
                              title,
                              area,
                              skeleton,
                              perimeter,
                              average,
                          }) => {
        return (
            <div className="bg-[#f1f1f1] flex gap-3 justify-between rounded-xl p-3 w-full">
                <div
                    className="shadow-xl bg-white rounded-xl flex flex-col items-center pb-2 relative"
                    style={{width: "5rem", height: "5rem"}}
                >
                    <img
                        src={icon}
                        alt="Wrinkle Icon"
                        className="w-full"
                        style={{objectFit: "cover", width: "3rem", height: "3rem"}}
                    />
                    <p>{title}</p>
                </div>
                <div className="flex flex-row gap-3 items-center w-full justify-evenly">
                    <div className="flex flex-col gap-2">
                        <p>
                            Area: {area}mm<sup>2</sup>
                        </p>
                        <p>Perimeter: {perimeter}mm</p>
                    </div>

                    <Divider
                        type="vertical"
                        style={{height: "100%", borderColor: "#888888"}}
                    />

                    <div className="flex flex-col gap-2">
                        <p>Skeleton Length: {skeleton}mm</p>
                        <p>Avg. Width: {average}mm</p>
                    </div>
                </div>
            </div>
        );
    };

    let analysisPath = null;

    if (analysisFiles.length === 0) {
        analysisPath = files.length > 0 ? "../analysis" : "../analysis/file-upload";
    }

    return (
        <DashboardWrapper>
            <Header>
                <IconContext.Provider value={{style: {cursor: "pointer"}}}>
                    <FaArrowLeft onClick={() => navigate(-1)}/>
                </IconContext.Provider>
                <h3>{t("ai_analysis")}</h3>
                {analysisFiles.length > 0 && (
                    <>
                        <AddButtonWithIcon
                            isTextHiddenOnMobile={true}
                            type="primary"
                            text={"Select File"}
                            onClick={() => {
                                navigate("../analysis");
                            }}
                        />
                    </>
                )}
            </Header>
            {
                isLoading ? <LoadingSpinner/> : (
                    <>
                        {analysisPath && (
                            <PlaceholderView
                                image={FaceIdIcon}
                                title={t("no_wrinkle_analysis")}
                                description={t("wrinkle_description")}
                                buttonText={t("analyze_now")}
                                onButtonClick={() => navigate(analysisPath)}
                            />
                        )}
                        {isCompare && (
                            <DivRow maxWidth={320} onClick={clearCompare}>
                                <h4>{t("select_file_to_compare")}</h4>
                                <IconContext.Provider value={{style: {cursor: "pointer"}}}>
                                    <FaXmark/>
                                </IconContext.Provider>
                            </DivRow>
                        )}
                        <Images>
                            {groupFiles &&
                                Object.entries(groupFiles).map(([key, data]) => {
                                    console.log('groupFiles', groupFiles);
                                    console.log('groupFiles key', key, data);
                                    if (data.length > 1) {
                                        const len = data.length;
                                        console.log('data', data);
                                        return (<AnalysisCard
                                            key={key}
                                            isGrouped={true}

                                            original_image_url={data[len - 1]?.files["original_image.jpg"]}
                                            detections_url={data[len - 1]?.files["detections.json"]}
                                            full_detail={data}
                                            visualization_url={data[len - 1]?.files["visualization.jpg"]}
                                            created_at={data[len - 1]?.created_at}

                                            onClick={() => {
                                                toggleModal(key);
                                            }
                                            }
                                        />)
                                    } else {
                                        switch (data[0]?.analysis_type) {
                                            case 1:
                                                return (
                                                    <AnalysisCard
                                                        key={key}
                                                        original_image_url={data[0]?.files["original_image.png"]}
                                                        mask_url={data[0]?.files["wrinkle_mask.png"]}
                                                        full_detail={data}
                                                        grading_results={data[0]?.files["grading_results.json"]}
                                                        created_at={data[0]?.created_at}
                                                        onClick={() => {
                                                            toggleModal(key);
                                                        }
                                                        }
                                                    />
                                                );
                                            case 2:
                                                return (
                                                    <AnalysisCard
                                                        key={key}
                                                        original_image_url={data[0]?.files["original_image.jpg"]}
                                                        detections_url={data[0]?.files["detections.json"]}
                                                        full_detail={data}
                                                        visualization_url={data[0]?.files["visualization.jpg"]}
                                                        created_at={data[0]?.created_at}
                                                    />
                                                );
                                            default:
                                                return null;
                                        }
                                    }
                                })}
                        </Images>
                    </>
                )
            }

            {/* File details modal */}
            {selectedAnalysis && (
                <Modal
                    title={t("analysis")}
                    footer={null}
                    onCancel={toggleModal}
                    open={isOpen}
                    width={{
                        xs: '90%',
                        sm: '90%',
                        md: '90%',
                        lg: '1000px',
                        xl: '1000px',
                        xxl: '1000px',
                    }}
                >
                    <Carousel
                        infinite={false}
                        draggable={true}
                        ref={slider}
                    >
                        {selectedAnalysis.map((file, index) => {
                            switch (file.analysis_type) {
                                case 1:
                                    return (
                                        <div key={index} className="h-full flex flex-col justify-center">
                                            <div style={contentStyle}>
                                                <h3>{t("wrinkles_analysis")}</h3>
                                                <WrinklesComponent
                                                    original_image_url={file.files["original_image.png"]}
                                                    mask_url={file.files["wrinkle_mask.png"]}
                                                    full_detail={file}
                                                    grading_results={file.files["grading_results.json"]}
                                                    created_at={file.created_at}
                                                />
                                            </div>
                                        </div>
                                    );
                                case 2:
                                    return (
                                        <div key={index} className="h-full flex flex-col justify-center">
                                            <div style={contentStyle}>
                                                <h3>{t("skin_lesion_analysis")}</h3>
                                                <AcneDetailsComponent
                                                    original_image_url={file.files["original_image.jpg"]}
                                                    detections_url={file.files["detections.json"]}
                                                    full_detail={file}
                                                    visualization_url={file.files["visualization.jpg"]}
                                                    created_at={file.created_at}
                                                />
                                            </div>
                                        </div>
                                    );
                                default:
                                    return null;
                            }
                        })}
                    </Carousel>
                </Modal>
            )}

        </DashboardWrapper>
    );
};

const WrinkleWrapper = styled.div`
    * {
        color: black;
    }
`;


const contentStyle = {
    // margin: '0 32px',
};

export default Wrinkles;
